import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  ChangeDetectorRef
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ERoleAdminString } from 'src/app/shared/data/role.enum';
import { EModuleConfigType, NAVIGATION_CONFIG } from '../../setting-module/enum/enum-type';
import { EFieldOptionsType } from '../../../models/field-options-type.enum';
import { isNumericType, isUnknownType } from '../../setting-module/enum/module-config.utils';
import { IModule, ModuleConfigSchema, PathDefinition } from '../../../models/setup.interface';
import { ModuleService } from '../../../services/module.service';
import { PATHS } from '../../setting-module/const/paths.const';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { CustomQuestionnaireService } from 'src/app/pages/questionarios/services/custom-questionnaire.service';
import { ICustomQuestionnaire } from '../../../models/questionnaire.interface';
import { NpsSurveyService } from 'src/app/pages/pesquisa-nps/services/nps-survey.service';
import { INpsSurvey } from 'src/app/pages/pesquisa-nps/models/nps-survey.interface';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalConfirmacaoService } from 'src/app/shared/services/modal-confirmacao.service';

interface ConfigField {
  description: string;
  type: EModuleConfigType;
  optionsType?: string;
}

type ConfigSchema = Record<string, ConfigField>;

@Component({
  selector: 'app-module-setting-modal',
  templateUrl: './module-setting-modal.component.html',
  styleUrls: ['./module-setting-modal.component.scss']
})
export class ModuleSettingModalComponent implements OnInit, OnChanges, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  ERoleAdminString = ERoleAdminString;
  formConfigModule: UntypedFormGroup;
  module?: ModuleConfigSchema;
  paths: PathDefinition[] = PATHS;
  modules: IModule[] = [];
  configSchema: ConfigSchema = {};
  configSchemaArray: { key: string; value: ConfigField }[] = [];
  enabledPathsOrdered: { key: string; order: number; name: string; showAsCard?: boolean }[] = [];
  @Input() moduleId!: string;
  @Input() setupId!: string;
  @Input() companyId?: string;
  @Input() initialConfig: any;
  @Output() closed = new EventEmitter<void>();
  @Output() save = new EventEmitter<{ moduleId: string; config: any }>();
  customQuestionnaires: ICustomQuestionnaire[] = [];
  npsSurveys: INpsSurvey[] = [];
  routeBooleanDefaults: Record<string, boolean> = {
    Module_Questionnaire: true
  };

  constructor(
    private readonly modulesService: ModuleService,
    private readonly customQuestionnaireService: CustomQuestionnaireService,
    private readonly npsSurveyService: NpsSurveyService,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalConfirmacaoService: ModalConfirmacaoService
  ) {
    this.formConfigModule = new UntypedFormGroup({
      name: new UntypedFormControl({ value: '', disabled: true }, [Validators.required])
    });
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['companyId'] && !changes['companyId'].firstChange) {
      this.maybeLoadCustomQuestionnaires();
      this.maybeLoadNpsSurveys();
    }
  }

  private async initializeForm(): Promise<void> {
    try {
      const moduleResponse = await this.modulesService.getModuleConfigSchema(this.moduleId).toPromise();

      if (moduleResponse?.data) {
        this.module = moduleResponse.data;
        const rawConfigSchema = this.module.configSchema || {};
        this.configSchema = Object.entries(rawConfigSchema)
          .filter(([key, value]) => value !== null)
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

        this.configSchemaArray = Object.entries(this.configSchema)
          .filter(([key, value]) => !Object.values(NAVIGATION_CONFIG).includes(key) && key !== 'enabledPaths')
          .map(([key, value]) => ({ key, value }));

        if (this.hasOptions(EFieldOptionsType.Modules)) {
          this.modulesService
            .getAll()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (response) => {
                this.modules = response.data;
                const epControl = this.getFormControl('enabledPaths');
                if (epControl) {
                  const cleaned = ((epControl.value || []) as { key: string; order: number }[])
                    .filter((item) => item.key !== 'Module_SkinCare')
                    .filter((item) => this.modules.some((m) => m.route === item.key))
                    .map((item, i) => ({ ...item, order: i + 1 }));
                  epControl.setValue(cleaned);
                }
                this.updateEnabledPathsOrdered();
                this.cdr.markForCheck();
              },
              error: (err) => {
                console.error('Erro ao carregar módulos:', err);
                this.modules = [];
                this.updateEnabledPathsOrdered();
                this.cdr.markForCheck();
              }
            });
        }

        await this.maybeLoadCustomQuestionnaires();
        await this.maybeLoadNpsSurveys();

        this.getFormControl('name')?.setValue(this.module.name);

        this.addConfigSchemaControls();

        if (this.initialConfig) {
          this.populateFormWithConfigModule(this.initialConfig);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
    }
  }

  private async maybeLoadCustomQuestionnaires(): Promise<void> {
    if (!this.hasOptions(EFieldOptionsType.CustomQuestionnaire) || !this.companyId) {
      if (!this.companyId) {
        this.customQuestionnaires = [];
      }
      return;
    }

    try {
      const questionnaires = await this.customQuestionnaireService.getByCompanyId(this.companyId).toPromise();
      this.customQuestionnaires = questionnaires ?? [];
    } catch (error) {
      console.error('Erro ao buscar questionários personalizados:', error);
    }
  }

  private async maybeLoadNpsSurveys(): Promise<void> {
    if (!this.hasOptions(EFieldOptionsType.NpsSurvey) || !this.companyId) {
      if (!this.companyId) {
        this.npsSurveys = [];
      }
      return;
    }
    try {
      const surveys = await this.npsSurveyService.getByCompanyId(this.companyId).toPromise();
      this.npsSurveys = surveys ?? [];
    } catch (error) {
      console.error('Erro ao buscar pesquisas NPS:', error);
      this.npsSurveys = [];
    }
  }

  // Método para adicionar os campos dinamicamente no formulário
  private addConfigSchemaControls(): void {
    const route = this.module!.route;
    const booleanDefault = this.routeBooleanDefaults[route] ?? false;

    Object.entries(this.configSchema).forEach(([key, field]) => {
      const typeInitialValue: Record<EModuleConfigType, any> = {
        [EModuleConfigType.String]: '',
        [EModuleConfigType.Boolean]: booleanDefault,
        [EModuleConfigType.Array]: [],
        [EModuleConfigType.Number]: null,
        [EModuleConfigType.Int]: null,
        [EModuleConfigType.Integer]: null
      };

      // Sem required: campos que não aparecem no formulário (ex.: enabledPaths) travavam o salvar sem
      // nenhum aviso. Os campos visíveis vazios pedem confirmação em saveModuleSettings.
      const initialValue = typeInitialValue[field.type] ?? '';
      const control = new UntypedFormControl(initialValue);

      this.formConfigModule.addControl(key, control);
    });
  }

  private populateFormWithConfigModule(configModule: any): void {
    if (!configModule) {
      return;
    }

    Object.entries(configModule).forEach(([key, value]) => {
      const control = this.getFormControl(key);
      if (control) {
        control.setValue(value);
      }
    });
  }

  // Mesma regra da tela de configuração da edição: campo visível vazio pede confirmação. Campos que
  // não aparecem no formulário (ex.: enabledPaths) ficam de fora, porque o usuário não tem como
  // preenchê-los.
  saveModuleSettings(): void {
    const formValues = this.formConfigModule.getRawValue();

    const emptyVisibleFields = this.configSchemaArray.filter((field) => {
      const value = formValues[field.key];
      return value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0);
    });

    if (emptyVisibleFields.length === 0) {
      this.emitConfig();
      return;
    }

    this.modalConfirmacaoService
      .abrirModalConfirmacao({
        mensagem: 'Existem campos não preenchidos. Deseja salvar mesmo assim?',
        exibeBotaoConfirma: true,
        exibeBotaoCancela: true,
        textoBotaoConfirma: 'Sim',
        textoBotaoCancela: 'Não'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe((confirmado) => {
        if (confirmado) {
          this.emitConfig();
        }
      });
  }

  private emitConfig(): void {
    const config = this.formConfigModule.getRawValue();
    delete config.name;
    this.save.emit({ moduleId: this.moduleId, config });
  }

  isNumericType = isNumericType;
  isUnknownType = isUnknownType;

  private hasOptions(optionValue: EFieldOptionsType): boolean {
    return Object.values(this.configSchema).some((field: any) => field.optionsType === optionValue);
  }

  onModuleCheckboxChange(event: any, key: string, route: string): void {
    const control = this.getFormControl(key);
    if (!control) return;

    const currentValue: string[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.includes(route)) {
        control.setValue([...currentValue, route]);
      }
    } else {
      control.setValue(currentValue.filter((r) => r !== route));
      this.toggleModuleEnabledPath(route, false);
    }
  }

  private updateEnabledPathsOrdered(): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) {
      this.enabledPathsOrdered = [];
      return;
    }
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    const cleaned = val
      .filter((item) => item.key !== 'Module_SkinCare')
      .filter((item) => this.modules.some((m) => m.route === item.key))
      .sort((a, b) => a.order - b.order)
      .map((item, i) => ({ key: item.key, order: i + 1, showAsCard: item.showAsCard ?? false }));
    control.setValue(cleaned, { emitEvent: false });
    this.enabledPathsOrdered = cleaned.map((item) => ({
      ...item,
      name: this.modules.find((m) => m.route === item.key)?.name || item.key
    }));
  }

  dropEnabledPaths(event: CdkDragDrop<any[]>): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    moveItemInArray(this.enabledPathsOrdered, event.previousIndex, event.currentIndex);
    control.setValue(
      this.enabledPathsOrdered.map((item, i) => ({
        key: item.key,
        order: i + 1,
        showAsCard: item.showAsCard ?? false
      }))
    );
    this.updateEnabledPathsOrdered();
  }

  isModuleEnabledPath(moduleRoute: string): boolean {
    const control = this.getFormControl('enabledPaths');
    if (!control) return false;
    const val: { key: string; order: number }[] = control.value || [];
    return val.some((item) => item.key === moduleRoute);
  }

  toggleModuleEnabledPath(moduleRoute: string, enabled: boolean): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    if (enabled) {
      if (!val.some((item) => item.key === moduleRoute)) {
        control.setValue([...val, { key: moduleRoute, order: val.length + 1, showAsCard: false }]);
      }
    } else {
      const filtered = val.filter((item) => item.key !== moduleRoute);
      control.setValue(filtered.map((item, i) => ({ ...item, order: i + 1 })));
    }
    this.updateEnabledPathsOrdered();
  }

  // Indica se o módulo do fluxo de saúde também deve aparecer como card avulso no Intro.
  isModuleShowAsCard(moduleRoute: string): boolean {
    const control = this.getFormControl('enabledPaths');
    if (!control) return false;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    return val.find((item) => item.key === moduleRoute)?.showAsCard ?? false;
  }

  // Liga/desliga o flag showAsCard mantendo o módulo dentro do fluxo de saúde.
  toggleModuleShowAsCard(moduleRoute: string, showAsCard: boolean): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;
    const val: { key: string; order: number; showAsCard?: boolean }[] = control.value || [];
    const updated = val.map((item) => (item.key === moduleRoute ? { ...item, showAsCard } : item));
    control.setValue(updated);
    this.updateEnabledPathsOrdered();
  }

  dropPaths(event: CdkDragDrop<any[]>, fieldKey: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const selectedPaths = [...control.value];

    moveItemInArray(selectedPaths, event.previousIndex, event.currentIndex);

    const updatedPaths = selectedPaths.map((path, index) => ({
      ...path,
      order: index + 1
    }));

    control.setValue(updatedPaths);
  }

  onPathCheckboxChange(event: any, fieldKey: string, pathKey: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const currentValue: { key: string; order: number }[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.some((item) => item.key === pathKey)) {
        control.setValue([...currentValue, { key: pathKey, order: currentValue.length + 1 }]);
      }
    } else {
      control.setValue(currentValue.filter((item) => item.key !== pathKey));
    }
  }

  onOrderChange(event: any, pathKey: string): void {
    const control = this.getFormControl('enabledPaths');
    if (!control) return;

    const order = parseInt(event.target.value, 10) || 0;
    const currentValue: { key: string; order: number }[] = control.value || [];

    const updatedValue = currentValue.map((item) => (item.key === pathKey ? { ...item, order } : item));

    control.setValue(updatedValue);
  }

  isPathSelected(key: string, pathKey: string): boolean {
    const control = this.getFormControl(key);
    if (!control) return false;
    const currentValue: { key: string; order: number }[] = control.value || [];
    return currentValue.some((item) => item.key === pathKey);
  }

  getPathName(pathKey: string): string {
    return this.paths.find((p) => p.key === pathKey)?.name || pathKey;
  }

  getOrder(key: string, pathKey: string): number {
    const control = this.getFormControl(key);
    if (!control) return 0;
    const currentValue: { key: string; order: number }[] = control.value || [];
    const selectedPath = currentValue.find((item) => item.key === pathKey);
    return selectedPath ? selectedPath.order : 0;
  }

  onQuestionnaireCheckboxChange(event: any, fieldKey: string, questionnaireId: string): void {
    const control = this.getFormControl(fieldKey);
    if (!control) return;

    const currentValue: { id: string; order: number }[] = control.value || [];

    if (event.target.checked) {
      if (!currentValue.some((item) => item.id === questionnaireId)) {
        control.setValue([...currentValue, { id: questionnaireId, order: currentValue.length + 1 }]);
      }
    } else {
      control.setValue(currentValue.filter((item) => item.id !== questionnaireId));
    }
  }

  isQuestionnaireSelected(fieldKey: string, questionnaireId: string): boolean {
    const control = this.getFormControl(fieldKey);
    if (!control) return false;
    const currentValue: { id: string; order: number }[] = control.value || [];
    return currentValue.some((item) => item.id === questionnaireId);
  }

  getQuestionnaireName(questionnaireId: string): string {
    return this.customQuestionnaires.find((q) => q.id === questionnaireId)?.name || questionnaireId;
  }

  getControl(fieldKey: string): UntypedFormControl | null {
    return this.getFormControl(fieldKey);
  }

  private getFormControl(fieldKey: string): UntypedFormControl | null {
    if (!fieldKey) {
      return null;
    }

    const control = this.formConfigModule.get(fieldKey) as UntypedFormControl | null;
    if (control) {
      return control;
    }

    const controls = this.formConfigModule.controls as Record<string, UntypedFormControl>;
    return controls[fieldKey] ?? null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
